
export interface CDNConfig {
  provider: 'cloudinary' | 'imagekit' | 'custom';
  cloudName?: string; // for Cloudinary
  publicKey?: string; // for ImageKit
  endpoint?: string; // for custom CDN
  transformations?: Record<string, any>;
}

export interface ImageTransformation {
  width?: number;
  height?: number;
  quality?: number;
  format?: 'webp' | 'jpeg' | 'png' | 'avif';
  crop?: 'fill' | 'fit' | 'scale' | 'crop';
  gravity?: 'center' | 'north' | 'south' | 'east' | 'west';
  blur?: number;
  sharpen?: number;
}

/**
 * Generate CDN URL based on provider and transformations
 */
export const generateCDNUrl = (
  originalUrl: string,
  transformations: ImageTransformation,
  config: CDNConfig
): string => {
  if (!originalUrl || originalUrl === '/placeholder.svg') {
    return originalUrl;
  }

  switch (config.provider) {
    case 'cloudinary':
      return generateCloudinaryUrl(originalUrl, transformations, config);
    case 'imagekit':
      return generateImageKitUrl(originalUrl, transformations, config);
    case 'custom':
      return generateCustomCDNUrl(originalUrl, transformations, config);
    default:
      return originalUrl;
  }
};

/**
 * Generate Cloudinary URL with transformations
 */
const generateCloudinaryUrl = (
  url: string,
  transformations: ImageTransformation,
  config: CDNConfig
): string => {
  if (!config.cloudName) return url;

  try {
    const transforms = [];
    
    if (transformations.width) transforms.push(`w_${transformations.width}`);
    if (transformations.height) transforms.push(`h_${transformations.height}`);
    if (transformations.quality) transforms.push(`q_${transformations.quality}`);
    if (transformations.format) transforms.push(`f_${transformations.format}`);
    if (transformations.crop) transforms.push(`c_${transformations.crop}`);
    if (transformations.gravity) transforms.push(`g_${transformations.gravity}`);
    if (transformations.blur) transforms.push(`e_blur:${transformations.blur}`);
    if (transformations.sharpen) transforms.push(`e_sharpen:${transformations.sharpen}`);

    const transformString = transforms.join(',');
    
    // If it's already a Cloudinary URL, modify it
    if (url.includes('cloudinary.com')) {
      return url.replace(/\/image\/upload\/[^/]*\//, `/image/upload/${transformString}/`);
    }
    
    // Convert external URL to Cloudinary fetch URL
    const encodedUrl = encodeURIComponent(url);
    return `https://res.cloudinary.com/${config.cloudName}/image/fetch/${transformString}/${encodedUrl}`;
  } catch (error) {
    console.error('Error generating Cloudinary URL:', error);
    return url;
  }
};

/**
 * Generate ImageKit URL with transformations
 */
const generateImageKitUrl = (
  url: string,
  transformations: ImageTransformation,
  config: CDNConfig
): string => {
  if (!config.endpoint) return url;

  try {
    const params = new URLSearchParams();
    
    if (transformations.width) params.set('tr', `w-${transformations.width}`);
    if (transformations.height) params.append('tr', `h-${transformations.height}`);
    if (transformations.quality) params.append('tr', `q-${transformations.quality}`);
    if (transformations.format) params.append('tr', `f-${transformations.format}`);
    if (transformations.crop) params.append('tr', `c-${transformations.crop}`);
    if (transformations.blur) params.append('tr', `bl-${transformations.blur}`);

    const transformString = params.toString().replace(/tr=/g, '').replace(/&tr/g, ',');
    
    // If it's already an ImageKit URL, modify it
    if (url.includes('imagekit.io')) {
      return url.replace(/\/tr:[^/]*\//, `/tr:${transformString}/`);
    }
    
    // For external URLs, use ImageKit's fetch API
    const encodedUrl = encodeURIComponent(url);
    return `${config.endpoint}/tr:${transformString}/${encodedUrl}`;
  } catch (error) {
    console.error('Error generating ImageKit URL:', error);
    return url;
  }
};

/**
 * Generate custom CDN URL
 */
const generateCustomCDNUrl = (
  url: string,
  transformations: ImageTransformation,
  config: CDNConfig
): string => {
  if (!config.endpoint) return url;

  try {
    const urlObj = new URL(config.endpoint);
    
    if (transformations.width) urlObj.searchParams.set('w', transformations.width.toString());
    if (transformations.height) urlObj.searchParams.set('h', transformations.height.toString());
    if (transformations.quality) urlObj.searchParams.set('q', transformations.quality.toString());
    if (transformations.format) urlObj.searchParams.set('f', transformations.format);
    
    urlObj.searchParams.set('url', encodeURIComponent(url));
    
    return urlObj.toString();
  } catch (error) {
    console.error('Error generating custom CDN URL:', error);
    return url;
  }
};

/**
 * Detect best image format based on browser support
 */
export const detectBestFormat = (): 'avif' | 'webp' | 'jpeg' => {
  if (typeof window === 'undefined') return 'webp';
  
  // Check for AVIF support
  const avifCanvas = document.createElement('canvas');
  avifCanvas.width = 1;
  avifCanvas.height = 1;
  const avifSupported = avifCanvas.toDataURL('image/avif').indexOf('data:image/avif') === 0;
  
  if (avifSupported) return 'avif';
  
  // Check for WebP support
  const webpCanvas = document.createElement('canvas');
  webpCanvas.width = 1;
  webpCanvas.height = 1;
  const webpSupported = webpCanvas.toDataURL('image/webp').indexOf('data:image/webp') === 0;
  
  return webpSupported ? 'webp' : 'jpeg';
};
